import {
  Approval,
  CLType,
  CLTypeParsedListResult,
  CLTypeParsedResult,
  CLTypeTypeResult,
} from '@block-explorer/entities';
import {
  Args,
  Approval as ApprovalSDK,
  CLType as SDKCLType,
  CLTypeList,
  CLTypeOption,
  CLTypeParser,
  CLValue,
  EntryPointArg,
  Key,
  PublicKey,
  URef,
  CLTypeUInt8,
  CLTypeByteArray,
} from 'casper-js-sdk';
import { isValidAccountHash } from '@make-software/cspr-design';

export const stringifyCLTypeType = (
  type: string | CLType | CLTypeTypeResult | Record<string, unknown>
): string => {
  const getTypeFromNestedObj = (type) =>
    typeof type === 'object' ? Object.keys(type)[0] : type;

  const clValueType = getTypeFromNestedObj(type);
  const clValueInnerType = getTypeFromNestedObj(type[clValueType]);

  switch (clValueType) {
    case CLType.ByteArray:
      return `${clValueType}[${clValueInnerType}]`;
    case CLType.List:
      return `${clValueType}[${stringifyCLTypeType(type[clValueType])}]`;
    case CLType.Tuple1:
    case CLType.Tuple2:
    case CLType.Tuple3:
    case CLType.Map:
    case CLType.Key:
      return `${clValueType}`;
    case CLType.Option:
      return `${clValueType}(${stringifyCLTypeType(type[clValueType])})`;

    default:
      return `${clValueType}`;
  }
};

export const getArgTypeLabel = (arg: EntryPointArg): string =>
  stringifyCLTypeType(arg.clType?.toJSON?.() || arg.clType?.toString?.() || '');

export const getNestedCLType = (typeLabel: string): string => {
  const listMatch = typeLabel.match(/^List\[(.+)\]$/);
  const optionMatch = typeLabel.match(/^Option\((.+)\)$/);

  if (listMatch) {
    return CLType.List;
  }

  if (optionMatch) {
    return CLType.Option;
  }

  return typeLabel;
};

export const isOptionalParameter = (typeLabel: string): boolean =>
  getNestedCLType(typeLabel) === CLType.Option;

export const isKeyParameter = (typeLabel: string): boolean =>
  typeLabel === CLType.Key || typeLabel === `Option(${CLType.Key})`;

export const isListU8Parameter = (typeLabel: string): boolean =>
  typeLabel === `${CLType.List}[${CLType.U8}]`;

export const isByteArrayParameter = (typeLabel: string): boolean =>
  /^ByteArray\[\d+\]$/.test(typeLabel);

const validateUnsignedInteger = (value: string, typeLabel: string) => {
  if (!/^\d+$/.test(value.trim())) {
    throw new Error(`${typeLabel} must be a non-negative integer`);
  }
};

/**
 * Creates a numeric CLValue after validating the original input syntax and
 * using SDK serialization to enforce the type's numeric bounds.
 *
 * @param value - Trimmed base-10 integer string entered by the user.
 * @param typeLabel - CL type name used in validation errors.
 * @param signed - Whether a leading minus sign is allowed.
 * @param createCLValue - SDK factory for the requested numeric CL type.
 * @returns The validated numeric CLValue.
 * @throws If the value is not a valid decimal integer or is out of bounds.
 */
const createValidatedNumericCLValue = (
  value: string,
  typeLabel: string,
  signed: boolean, // Unsigned types such as U32 use and therefore accept only zero or positive integers.
  createCLValue: (value: string) => CLValue
): CLValue => {
  if (signed) {
    if (!/^-?\d+$/.test(value)) {
      throw new Error(`${typeLabel} must be an integer`);
    }
  } else {
    validateUnsignedInteger(value, typeLabel);
  }

  const clValue = createCLValue(value);

  // Numeric constructors accept out-of-range values. Serialization performs
  // the SDK's actual signedness and bit-width validation.
  try {
    clValue.bytes();
  } catch (error) {
    if (
      error instanceof Error &&
      error.message.includes('value out-of-bounds')
    ) {
      throw new Error(`${typeLabel} value is out of bounds`);
    }

    throw error;
  }

  return clValue;
};

const hexToBytes = (value: string): Uint8Array => {
  const normalized = value.startsWith('0x') ? value.slice(2) : value;

  if (!/^[a-fA-F0-9]*$/.test(normalized) || normalized.length % 2 !== 0) {
    throw new Error('Value must be an even-length hex string');
  }

  const bytes = new Uint8Array(normalized.length / 2);

  for (let i = 0; i < normalized.length; i += 2) {
    bytes[i / 2] = parseInt(normalized.slice(i, i + 2), 16);
  }

  return bytes;
};

export const convertHexToByteArrayInput = (value: string): string =>
  JSON.stringify(Array.from(hexToBytes(value.trim())));

const parseListU8Value = (value: string): number[] => {
  if (!value.startsWith('[')) {
    return Array.from(hexToBytes(value));
  }

  let bytes: unknown;

  try {
    bytes = JSON.parse(value);
  } catch {
    throw new Error('Value must be a hexadecimal string or byte array');
  }

  if (
    !Array.isArray(bytes) ||
    !bytes.every((byte) => Number.isInteger(byte) && byte >= 0 && byte <= 255)
  ) {
    throw new Error('Each byte must be an integer between 0 and 255');
  }

  return bytes;
};

export const normalizeKeyArgument = (value: string): string => {
  const normalizedValue = value.trim();

  if (isValidAccountHash(normalizedValue)) {
    return `account-hash-${normalizedValue.toLowerCase()}`;
  }

  try {
    return PublicKey.fromHex(normalizedValue).accountHash().toPrefixedString();
  } catch {
    return normalizedValue;
  }
};

export const parseCLValue = (
  value: string,
  jsonClType: string | Record<string, unknown>
): CLValue => {
  const trimmedValue = value.trim();
  const sdkType = CLTypeParser.fromRawJson(jsonClType);

  if (!trimmedValue) {
    if (sdkType instanceof CLTypeOption) {
      return CLValue.newCLOption(null, sdkType.inner);
    }
    throw new Error('Value is required');
  }

  return parseNestedCLValue(value, sdkType);
};

const parseNestedCLValue = (value: unknown, sdkType: SDKCLType): CLValue => {
  if (sdkType instanceof CLTypeOption) {
    return CLValue.newCLOption(
      value === null ? null : parseNestedCLValue(value, sdkType.inner),
      sdkType.inner
    );
  }

  if (value === null) {
    throw new Error('Value is required');
  }

  if (sdkType instanceof CLTypeList) {
    return parseListValue(
      Array.isArray(value) ? JSON.stringify(value) : String(value),
      sdkType
    );
  }

  return parseCLValueByType(String(value), sdkType);
};

const parseListValue = (value: string, type: CLTypeList): CLValue => {
  const typeLabel = stringifyCLTypeType(type.toJSON());
  if (isListU8Parameter(typeLabel)) {
    return CLValue.newCLList(
      CLTypeUInt8,
      parseListU8Value(value?.trim()).map((byte) => CLValue.newCLUint8(byte))
    );
  }

  let items = value;

  try {
    items = JSON.parse(value);
  } catch {
    throw new Error(`${typeLabel} must be a JSON array`);
  }

  if (!Array.isArray(items)) {
    throw new Error(`${typeLabel} must be a JSON array`);
  }

  return CLValue.newCLList(
    type.elementsType,
    items.map((item) => parseNestedCLValue(item, type.elementsType))
  );
};

const parseCLValueByType = (value: string, sdkType: SDKCLType): CLValue => {
  const typeLabel = stringifyCLTypeType(sdkType.toJSON());
  const trimmedValue = value?.trim();
  const baseType = sdkType.getName();

  switch (baseType) {
    case CLType.String:
      return CLValue.newCLString(value);
    case CLType.Bool:
      if (!['true', 'false'].includes(trimmedValue.toLowerCase())) {
        throw new Error('Bool must be true or false');
      }
      return CLValue.newCLValueBool(trimmedValue.toLowerCase() === 'true');
    case CLType.I32:
      return createValidatedNumericCLValue(
        trimmedValue,
        typeLabel,
        true,
        CLValue.newCLInt32
      );
    case CLType.I64:
      return createValidatedNumericCLValue(
        trimmedValue,
        typeLabel,
        true,
        CLValue.newCLInt64
      );
    case CLType.U8:
      return createValidatedNumericCLValue(
        trimmedValue,
        typeLabel,
        false,
        CLValue.newCLUint8
      );
    case CLType.U32:
      return createValidatedNumericCLValue(
        trimmedValue,
        typeLabel,
        false,
        CLValue.newCLUInt32
      );
    case CLType.U64:
      return createValidatedNumericCLValue(
        trimmedValue,
        typeLabel,
        false,
        CLValue.newCLUint64
      );
    case CLType.U128:
      return createValidatedNumericCLValue(
        trimmedValue,
        typeLabel,
        false,
        CLValue.newCLUInt128
      );
    case CLType.U256:
      return createValidatedNumericCLValue(
        trimmedValue,
        typeLabel,
        false,
        CLValue.newCLUInt256
      );
    case CLType.U512:
      return createValidatedNumericCLValue(
        trimmedValue,
        typeLabel,
        false,
        CLValue.newCLUInt512
      );
    case CLType.PublicKey:
      return CLValue.newCLPublicKey(PublicKey.fromHex(trimmedValue));
    case CLType.URef:
      return CLValue.newCLUref(URef.fromString(trimmedValue));
    case CLType.Key:
      return CLValue.newCLKey(Key.newKey(normalizeKeyArgument(trimmedValue)));
    case CLType.ByteArray: {
      const bytes = Uint8Array.from(parseListU8Value(trimmedValue));
      const expectedSize = (sdkType as CLTypeByteArray).size;

      if (bytes.length !== expectedSize) {
        throw new Error(
          `Expected ${expectedSize} bytes (${expectedSize * 2} hex characters, excluding optional 0x prefix)`
        );
      }

      return CLValue.newCLByteArray(bytes);
    }
    case CLType.Any:
      return CLValue.newCLByteArray(hexToBytes(trimmedValue));
    default:
      throw new Error(`${typeLabel} is not supported yet`);
  }
};

export const stringifyCLTypeValue = ({
  type,
  value,
  balance,
}: {
  type?: CLTypeTypeResult | string;
  value: CLTypeParsedResult | null;
  balance?: string | null;
}) => {
  if (!type) {
    return Array.isArray(value)
      ? value.join(', ')
      : typeof value === 'object'
        ? JSON.stringify(value)
        : value;
  }

  const getTypeFromNestedObj = (type) =>
    typeof type === 'object' ? Object.keys(type)[0] : type;

  const clValueType = getTypeFromNestedObj(type);
  const clValueInnerType = getTypeFromNestedObj(type[clValueType]);

  switch (clValueType) {
    case CLType.Unit:
      return balance;

    case CLType.List:
      return (value as CLTypeParsedListResult)
        ?.map((listItem) =>
          stringifyCLTypeValue({
            type: clValueInnerType,
            value: listItem,
          })
        )
        .join(', ');

    case CLType.Tuple1:
    case CLType.Tuple2:
    case CLType.Tuple3:
      return (value as CLTypeParsedListResult)?.join(', ');

    case CLType.Option:
      return stringifyCLTypeValue({
        type: clValueInnerType,
        value: value,
      });
    case CLType.Result:
    case CLType.Map:
      return JSON.stringify(value);

    case CLType.Bool:
      return value !== null && value !== undefined ? value.toString() : null;

    default:
      return value;
  }
};

export const convertTransactionArgsToObj = (
  args: Args
): Record<string, any> => {
  if (!args) {
    return {};
  }

  const argsKeys = [...args.args.keys()];

  return argsKeys.reduce((prev, curr) => {
    return {
      ...prev,
      [curr]: args.getByName(curr)?.toJSON(),
    };
  }, {});
};

export const convertApprovalsFromBytesToString = (
  approvals: ApprovalSDK[]
): Approval[] => {
  if (!approvals || !approvals.length) {
    return [];
  }

  return approvals.map((approval) => {
    return {
      signer: approval.signer.toHex(),
      signature: approval.signature.toJSON(),
    };
  }) as Approval[];
};
